import { useEffect, useState } from 'react';
import { NAV_ITEMS } from '@/data/mockData';
import type { NavItem } from '@/types';

export type PermissionValue = true | 'view' | false;
type PermissionRole = 'boss' | 'manager' | 'sales' | 'retail' | 'driver' | 'writer' | 'shopkeeper';
type PermissionRow = { module: string } & Record<PermissionRole, PermissionValue>;

export const PERMISSION_STORAGE_KEY = 'changfa_permission_matrix';
export const PERMISSIONS_CHANGED_EVENT = 'changfa:permissions-changed';

const ROLES: PermissionRole[] = ['boss', 'manager', 'sales', 'retail', 'driver', 'writer', 'shopkeeper'];
const DEFAULT_PERMISSION_MATRIX: PermissionRow[] = [
  { module: '基础数据', boss: true, manager: true, sales: false, retail: false, driver: false, writer: 'view', shopkeeper: 'view' },
  { module: '进货管理', boss: true, manager: true, sales: false, retail: false, driver: false, writer: false, shopkeeper: false },
  { module: '库存管理', boss: true, manager: true, sales: 'view', retail: false, driver: 'view', writer: 'view', shopkeeper: 'view' },
  { module: '销售管理', boss: true, manager: true, sales: true, retail: false, driver: false, writer: true, shopkeeper: 'view' },
  { module: '报价单', boss: true, manager: true, sales: true, retail: false, driver: false, writer: true, shopkeeper: false },
  { module: '零售收银', boss: true, manager: true, sales: false, retail: true, driver: false, writer: false, shopkeeper: true },
  { module: '财务管理', boss: true, manager: 'view', sales: false, retail: false, driver: false, writer: 'view', shopkeeper: false },
  { module: '地图配送', boss: true, manager: true, sales: true, retail: false, driver: true, writer: 'view', shopkeeper: false },
  { module: '条码打印', boss: true, manager: true, sales: true, retail: true, driver: false, writer: true, shopkeeper: true },
  { module: '数据分析', boss: true, manager: true, sales: false, retail: false, driver: false, writer: 'view', shopkeeper: 'view' },
  { module: '营销中心', boss: true, manager: false, sales: false, retail: false, driver: false, writer: false, shopkeeper: false },
  { module: '系统管理', boss: true, manager: false, sales: false, retail: false, driver: false, writer: false, shopkeeper: false },
];

const PAGE_MODULES: Record<string, string | undefined> = {
  'basic-data': '基础数据',
  purchase: '进货管理',
  inventory: '库存管理',
  sales: '销售管理',
  quotation: '报价单',
  retail: '零售收银',
  finance: '财务管理',
  map: '地图配送',
  barcode: '条码打印',
  analysis: '数据分析',
  'festival-promotion': '营销中心',
  system: '系统管理',
};

function isPermissionRole(role: string | undefined): role is PermissionRole {
  return !!role && ROLES.includes(role as PermissionRole);
}

function validPermission(value: unknown): value is PermissionValue {
  return value === true || value === false || value === 'view';
}

export function loadPermissionMatrix(): PermissionRow[] {
  try {
    const raw = localStorage.getItem(PERMISSION_STORAGE_KEY);
    if (!raw) return DEFAULT_PERMISSION_MATRIX;
    const saved = JSON.parse(raw) as unknown;
    if (!Array.isArray(saved)) return DEFAULT_PERMISSION_MATRIX;
    return DEFAULT_PERMISSION_MATRIX.map((fallback) => {
      const row = saved.find((item): item is Record<string, unknown> =>
        !!item && typeof item === 'object' && (item as Record<string, unknown>).module === fallback.module,
      );
      if (!row) return fallback;
      return ROLES.reduce<PermissionRow>((next, role) => {
        next[role] = validPermission(row[role]) ? row[role] : fallback[role];
        return next;
      }, { ...fallback });
    });
  } catch {
    return DEFAULT_PERMISSION_MATRIX;
  }
}

export function canAccessModule(role: string | undefined, module: string): boolean {
  if (!isPermissionRole(role)) return false;
  const row = loadPermissionMatrix().find((item) => item.module === module);
  return !!row && row[role] !== false;
}

export function canAccessAnalytics(role: string | undefined): boolean {
  return canAccessModule(role, '数据分析');
}

export function canAccessPage(role: string | undefined, page: string): boolean {
  const module = PAGE_MODULES[page];
  return module ? canAccessModule(role, module) : true;
}

export function getVisibleNavItems(role: string | undefined): NavItem[] {
  return NAV_ITEMS
    .map((item) => {
      const isParentAllowed = canAccessPage(role, item.path);
      const children = item.children?.filter((child) => canAccessPage(role, child.path)) || [];
      if (!isParentAllowed && children.length === 0) return null;
      return item.children ? { ...item, children } : item;
    })
    .filter((item): item is NavItem => item !== null);
}

export function usePermissionRevision(): number {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  return revision;
}
